import { api } from "@/lib/api";
import type { PermitDetail, PermitEvent, PermitStep } from "@/lib/permitProcesses";

export type DeregistrationVariant = "ukidanje" | "obustava";
export interface DeregistrationCandidate {
  worker_id: string;
  worker_name: string;
  termination_on: string;
  suggested_variant: DeregistrationVariant;
  process_id: string | null;
}
export interface DeregistrationAttachment { key: string; name: string; dokument_id: string | null; missing: boolean }
export interface DeregistrationSubmission { authority_id: string; submitted_on: string; proof_document_id: string }
export interface DeregistrationDetail {
  process: PermitDetail["process"];
  case_number: string;
  variant: DeregistrationVariant;
  termination_on: string;
  permit_id: string | null;
  source_process_id: string | null;
  steps: PermitStep[];
  events: PermitEvent[];
  attachments: DeregistrationAttachment[];
  letters: { code: string; authority_id: string; authority_name: string; document_id: string | null }[];
  submissions: DeregistrationSubmission[];
}
export async function getDeregistrationCandidates(workerId?: string, signal?: AbortSignal): Promise<DeregistrationCandidate[]> {
  if (workerId) {
    const { data } = await api.get<DeregistrationCandidate | null>(`/radnici/${workerId}/dozvole/odjava`, { signal });
    return data ? [data] : [];
  }
  return (await api.get<DeregistrationCandidate[]>("/dozvole/odjave", { signal })).data;
}
export async function startDeregistration(workerId: string): Promise<DeregistrationDetail> {
  return (await api.post<DeregistrationDetail>(`/radnici/${workerId}/dozvole/odjava`, {})).data;
}
export interface DeregistrationHistory { process_id: string; case_number: string; termination_on: string; status: string }
export async function getDeregistrationHistory(workerId: string, signal?: AbortSignal): Promise<DeregistrationHistory[]> {
  return (await api.get<DeregistrationHistory[]>(`/radnici/${workerId}/dozvole/odjave`, { signal })).data;
}
export async function getDeregistration(processId: string, signal?: AbortSignal): Promise<DeregistrationDetail> {
  return (await api.get<DeregistrationDetail>(`/dozvole/odjave/${processId}`, { signal })).data;
}
export async function setDeregistrationVariant(processId: string, variant: DeregistrationVariant) {
  await api.patch(`/dozvole/odjave/${processId}/varijanta`, { variant });
}
export async function setDeregistrationAttachment(processId: string, key: string, dokument_id: string | null) {
  await api.put(`/dozvole/odjave/${processId}/prilozi/${key}`, { dokument_id, missing: dokument_id === null });
}
export async function markDeregistrationReady(processId: string, completedOn?: string) {
  await api.post(`/dozvole/odjave/${processId}/spremno`, completedOn ? { completed_on: completedOn } : undefined);
}
export async function submitDeregistration(processId: string, data: DeregistrationSubmission & { missing_confirmed?: boolean }) {
  await api.post(`/dozvole/odjave/${processId}/predaja`, data);
}
export async function confirmDeregistration(processId: string, confirmed_on: string, proof_document_id: string) {
  await api.post(`/dozvole/odjave/${processId}/potvrda`, { confirmed_on, proof_document_id });
}
export async function printDeregistration(processId: string): Promise<Blob> {
  return (await api.post<Blob>(`/dozvole/odjave/${processId}/ispis`, {}, { responseType: "blob" })).data;
}
